import { ToastContainer } from "react-toastify";
import logo from "../../assets/logos/logoX.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateAccount } from "../../stores/Layout/createAccount";
import Eyes from "../../assets/icons/eye.svg";
import { showToastError, showToastSuccess } from "../../services/Toasts/toasts";
import {
  createAccount,
  createAccountWithOffice,
} from "../../services/Api/userService";
import microsoft from "../../assets/logos/microsoft.png";
import { useMsal } from "@azure/msal-react";
import GoogleLoginButton from "../../components/Layout/GoogleLoginButton";

const CreateAnAccount = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,
    title,
    setTitle,
    password,
    setPassword,
    verifyPassword,
    setVerifyPassword,
    passwordInputType,
    setPasswordInputType,
    acceptedCgu,
    setAcceptedCgu,
  } = useCreateAccount();
  const navigate = useNavigate();

  // Password visibility
  const togglePasswordVisibility = () => {
    setPasswordInputType(
      passwordInputType === "password" ? "text" : "password"
    );
  };

  const handleCreateAccount = () => {
    if (!acceptedCgu) return showToastError("Vous devez accepter les CGU.");
    if (password !== verifyPassword)
      return showToastError("Les deux mots de passe doivent être identiques !");

    const data = {
      firstName,
      lastName,
      tel: phone,
      titre: title,
      password: verifyPassword,
    };
    if (token !== "") {
      createAccount(data, token)
        .then(() => {
          showToastSuccess("Votre compte à bien été créé.");
          navigate("/");
        })
        .catch((err: any) => {
          if (
            err.response.data ===
            "email in token does not correspond to email in query"
          ) {
            showToastError(
              "Vous n'avez pas été invité à rejoindre Nexum. Vérifiez l'email de l'invitation."
            );
          } else {
            showToastError("Une erreur est survenue.");
          }
        });
    }
  };

  const { instance } = useMsal();

  // Login with office
  const handleSingInOffice = () => {
    instance
      .loginPopup({
        scopes: ["user.read"],
      })
      .then((response) => {
        createAccountWithOffice(response.idToken, response.accessToken, token)
          .then(() => {
            showToastSuccess("Votre compte a bien été créé.");
            navigate("/");
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        if (error.response.status === 500) {
          if (error.response.data === "email account not match") {
            showToastError(
              "Merci de vous connecter avec l'email de l'invitation."
            );
          } else {
            showToastError("Une erreur est survenue lors de la connexion.");
          }
        } else {
          showToastError("Une erreur est survenue lors de la connexion.");
        }
      });
  };

  return (
    <div className="relative min-h-dvh w-full overflow-x-hidden overflow-y-auto">
      <ToastContainer position="top-center" />
      
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100"></div>
      
      {/* Logo */}
      <div className="absolute left-6 top-5 z-30 hidden transition-transform duration-300 hover:scale-110 md:block lg:left-8 lg:top-6">
        <img src={logo} alt="logo Nexum" className="w-12 h-16 drop-shadow-lg" />
      </div>
      
      {/* Main container */}
      <main className="relative z-20 flex min-h-dvh w-full items-start justify-center sm:px-6 sm:py-8 lg:px-10">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleCreateAccount();
          }}
          className="relative z-20 flex min-h-dvh w-full max-w-3xl flex-col items-center overflow-hidden bg-white/95 px-5 py-5 backdrop-blur-sm sm:min-h-0 sm:rounded-2xl sm:border sm:border-white/20 sm:px-8 sm:py-7 sm:shadow-2xl sm:transition-all sm:duration-500 sm:hover:shadow-3xl lg:px-10"
        >
          {/* Decorative elements */}
          <div className="absolute -left-10 -top-10 z-10 hidden h-20 w-20 rotate-12 rounded-2xl border-2 border-dark-lavender bg-gradient-to-br from-light-lavender to-dark-lavender2 shadow-2xl transition-transform duration-300 sm:block"></div>
          
          {/* Header */}
          <div className="pb-5 pt-2 text-center sm:pb-7 sm:pt-3">
            <img src={logo} alt="Nexum" className="mx-auto mb-3 h-12 w-auto drop-shadow-md md:hidden" />
            <h1 className="mb-2 font-K2D text-2xl font-bold tracking-wide text-gray-700 sm:text-3xl 3xl:text-4xl">
              CRÉATION DE VOTRE COMPTE
            </h1>
            <div className="w-16 h-1 bg-gradient-to-r from-yellow-trio to-yellow-400 rounded-full mx-auto"></div>
            <p className="mx-auto mt-3 max-w-md px-1 font-BarlowCondensed text-sm leading-relaxed text-gray-600 sm:mt-4">
              Vous avez été invité à rejoindre Nexum, la plateforme d'échange de documents. 
              Merci de renseigner vos coordonnées personnelles.
            </p>
          </div>
          
          {/* Form fields */}
          <div className="w-full space-y-4 sm:space-y-6">
            {/* First row - First name and Last name */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="firstname"
                  className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
                >
                  Prénom
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    name="firstname"
                    id="firstname"
                    value={firstName}
                    required
                    onChange={(e) => setFirstName(e.target.value)}
                    className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                    placeholder="Votre prénom"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
                </div>
              </div>
              
              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="lastname"
                  className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
                >
                  Nom
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    name="lastname"
                    id="lastname"
                    value={lastName}
                    required
                    onChange={(e) => setLastName(e.target.value)}
                    className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                    placeholder="Votre nom"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
                </div>
              </div>
            </div>
            
            {/* Second row - Title and Phone */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="title"
                  className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
                >
                  Titre
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    name="title"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                    placeholder="Votre titre"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="phone"
                  className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
                >
                  Téléphone
                </label>
                <div className="relative group">
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                    placeholder="Votre téléphone"
                  />
                  <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
                </div>
              </div>
            </div>
            
            {/* Fourth row - Verify password */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <div className="space-y-1.5 sm:space-y-2">
                <label
                  htmlFor="password"
                  className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
                >
                  Mot de passe
                </label>
                <div className="relative group">
                  <input
                    type={passwordInputType}
                    name="password"
                    pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
                    id="password"
                    value={password}
                    required
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 pr-12 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                    onClick={togglePasswordVisibility}
                  >
                    <img src={Eyes} alt="oeil" className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity duration-200" />
                  </button>
                  <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
                </div>
              </div>
            <div className="space-y-1.5 sm:space-y-2">
              <label
                htmlFor="verifypassword"
                className="text-gray-700 text-sm 3xl:text-lg font-BarlowCondensed font-medium block"
              >
                Vérifier le mot de passe
              </label>
              <div className="relative group">
                <input
                  type={passwordInputType}
                  name="verifypassword"
                  id="verifypassword"
                  pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
                  value={verifyPassword}
                  required
                  onChange={(e) => setVerifyPassword(e.target.value)}
                  className="form-input-modern h-11 w-full rounded-xl border-2 border-gray-200 bg-gray-50 px-4 pr-12 text-base text-gray-700 shadow-sm transition-all duration-300 placeholder:text-gray-400 group-hover:shadow-md focus:border-yellow-trio focus:bg-white focus:outline-none sm:h-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                  onClick={togglePasswordVisibility}
                >
                  <img src={Eyes} alt="oeil" className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity duration-200" />
                </button>
                <div className="absolute inset-0 rounded-xl transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-yellow-trio/10 group-hover:to-yellow-400/10 pointer-events-none"></div>
              </div>
            </div>
            </div>
            {/* Password requirements */}
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 sm:p-4">
              <p className="font-BarlowCondensed text-xs leading-relaxed text-blue-800 min-[360px]:text-sm">
                <strong>Exigences du mot de passe :</strong> Minimum 8 caractères, une majuscule, 
                une minuscule, un chiffre et un caractère spécial.
              </p>
            </div>
            
            {/* CGU checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="cgu"
                id="cgu"
                checked={acceptedCgu}
                onChange={() => setAcceptedCgu(!acceptedCgu)}
                required
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-yellow-trio focus:ring-2 focus:ring-yellow-trio"
              />
              <label htmlFor="cgu" className="text-gray-700 text-sm font-BarlowCondensed">
                J'accepte les{" "}
                <button
                  type="button"
                  onClick={() => navigate("/cgu")}
                  className="text-yellow-trio hover:text-yellow-400 underline decoration-2 underline-offset-4 transition-colors duration-300"
                >
                  Conditions Générales d'Utilisation
                </button>
              </label>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-col-reverse items-center justify-center gap-3 pt-2 sm:flex-row sm:gap-4 sm:pt-4">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="min-h-11 w-full cursor-pointer rounded-xl bg-gray-100 px-8 py-2.5 font-BarlowCondensed font-medium text-gray-700 shadow-sm transition-all duration-300 hover:bg-red-500 hover:text-white hover:shadow-md sm:w-auto sm:py-3"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-modern min-h-11 w-full cursor-pointer rounded-xl border-0 bg-gradient-to-r from-yellow-trio to-yellow-400 px-8 py-2.5 font-BarlowCondensed font-semibold text-gray-700 shadow-lg transition-all duration-300 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-yellow-trio focus:ring-offset-2 sm:w-auto sm:py-3 sm:hover:scale-105"
              >
                Créer mon compte
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="my-5 flex w-full max-w-md items-center sm:my-6">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            <span className="px-4 text-gray-500 text-sm font-medium">ou</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* Social registration buttons */}
          <div className="w-full max-w-md space-y-3 pb-1 sm:space-y-4 sm:pb-0">
            <p className="text-center text-gray-600 text-sm font-BarlowCondensed">
              En procédant à votre enregistrement avec Office, vous acceptez automatiquement les CGU.
            </p>
            
            {/* <GoogleLoginButton /> */}

            <button
              type="button"
              className="group flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-sm transition-all duration-300 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md min-[360px]:text-base sm:h-12 sm:gap-3"
              onClick={handleSingInOffice}
            >
              <img
                src={microsoft}
                alt="logo microsoft"
                className="h-5 w-5 transition-transform duration-300 group-hover:scale-110"
              />
              <span>S'enregistrer avec Office</span>
            </button>
          </div>
          
          {/* Decorative element */}
          <div className="absolute -bottom-10 -right-10 z-10 hidden h-20 w-20 -rotate-12 rounded-2xl border-2 border-light-lavender bg-gradient-to-br from-light-lavender to-dark-lavender shadow-2xl transition-transform duration-300 sm:block"></div>
        </form>
      </main>
    </div>
  );
};

export default CreateAnAccount;
